import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { Store, select } from "@ngrx/store";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { combineLatest, finalize, map, Observable, takeUntil } from "rxjs";
import { BaseComponent } from "src/app/base.component";
import { Constants } from "src/app/shared/data/constants";
import { ExportData, RetentionRateData } from "src/app/shared/data/exportData";
import { Authorities } from "src/app/shared/enum/authorities";
import { AudienceType } from "src/app/shared/enum/chart/audiance";
import { TechnicalCardSearchMethodEnum } from "src/app/shared/enum/chart/technical-data";
import { FeatureName } from "src/app/shared/enum/feature-name.enum";
import { RefreshButtonState } from "src/app/shared/enum/refreshButtonState";
import { UserHistoryActionsEnum } from "src/app/shared/enum/userHistoryActions";
import { UserHistoryServicesEnum } from "src/app/shared/enum/userHistoryService";
import { DateFilter } from "src/app/shared/models/date-filte.interface";
import { BatchToggleFeatureElementDto } from "src/app/shared/models/features/batch-toggle-feature-element-dto.interface";
import { UserFeatureDto } from "src/app/shared/models/features/user-feature-dto.interface";
import { IberisActivities } from "src/app/shared/models/iberis/activities";
import { InputAudience } from "src/app/shared/models/input-audience.interface";
import { LPInfo } from "src/app/shared/models/location-partners/lpInfo";
import { AudienceStatisticsDto } from "src/app/shared/models/statistics/audienceStatisticsDto.interface";
import { RetentionRate } from "src/app/shared/models/statistics/retentionRate";
import { TechnicalCardSearchMethod } from "src/app/shared/models/statistics/technical-data";
import { SuccessDto } from "src/app/shared/models/success-dto";
import { RoleEnum } from "src/app/shared/models/user/role.interface";
import { SaveUserHistoryService } from "src/app/shared/services/history/saveHistory";
import { JwtService } from "src/app/shared/services/jwt.service";
import { AppStateInterface } from "src/app/store/appState.interface";
import { invokeAllFeatures } from "src/app/store/features/features.actions";
import { allFeaturesSelector } from "src/app/store/features/features.selectors";
import {
  invokeGetLocationPartnerByCompanyName,
  resultLPInfo,
} from "src/app/store/locationPartner/actions/get-locations.actions";
import { resultLPInfoSelector } from "src/app/store/locationPartner/location-partner.selectors";
import { resultSindingMail } from "src/app/store/mailing/mailing.actions";
import { sendMailSelector } from "src/app/store/mailing/mailing.selectors";
import {
  resultGlobalStatisticsForLP,
  resultRetentionRateByWeeks,
  invokeRetentionRateByWeeks,
  invokeLocationPartnerGlobalStatistics,
} from "src/app/store/statistics/statistics.actions";
import {
  loadingSummaryStatisticsSelector,
  resultRetentionRateByWeeksSelectors,
  loadingGlobalStaticsSelector,
  loadingRetentionRateSelector,
  resultGlobalStatisticsSelector,
} from "src/app/store/statistics/statistics.selectors";
import { UtilFunctions } from "src/app/utils/utilFunctions";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-location-statistics",
  templateUrl: "./location-statistics.component.html",
})
export class LocationStatisticsComponent
  extends BaseComponent
  implements OnInit
{
  @Output() retentionRate = new EventEmitter<RetentionRate | null>();
  @Output() search = new EventEmitter<TechnicalCardSearchMethod>();
  loading$: Observable<boolean | null>;

  consumers = {
    value: 0,
    title: "location.statistics.consumers",
    icon: "assets/identity/consummers.svg",
    unit: 0,
  };
  impression = {
    value: 0,
    title: "location.statistics.impression",
    icon: "assets/identity/eye-icon.svg",
    unit: 0,
  };

  reach = {
    value: 0,
    title: "inseights.details.engagementOverview.reach",
    icon: "assets/identity/uniqueViews.svg",
    unit: 0,
  };

  columnChart = {
    title: "Données démographiques",
    yaxisTitle: this.translate.instant("Charts.retentionRate.yaxisTitle"),
    xaxisTitle: this.translate.instant("Charts.retentionRate.weeks"),
  };

  @Output() inputAudience = new EventEmitter<InputAudience>();

  form: FormGroup;
  lpProfile$: Observable<LPInfo | null>;
  lpProfile: LPInfo;
  resultGlobalStatistics$: Observable<AudienceStatisticsDto | null>;
  resultGlobalStatistics: AudienceStatisticsDto = {
    cachedAt: new Date(),
    impressions: 0,
    retainedConsumers: 0,
    uniqueConsumers: 0,
    reach: 0
  };
  resultRetentionRate$: Observable<RetentionRate | null>;
  loadingGlobalStatics$: Observable<boolean | null>;

  dateFilter: DateFilter = {
    startDate: null,
    endDate: null,
  };

  companyActivities: IberisActivities[];

  retentionChart: ExportData[] = RetentionRateData;
  companyName: string | null;
  resultSendStatics$: Observable<SuccessDto | null>;
  retentionRateCachedAt: Date | null;
  cachedAt: Date | null;
  isLoading: boolean;
  loadingAllCardStats$: Observable<boolean>;
  forbidden: boolean | null = false;
  loadingProfile: boolean = true;
  fetchingCardState: RefreshButtonState = RefreshButtonState.IDLE;
  fetchingRetentionRateState: RefreshButtonState = RefreshButtonState.IDLE;

  roleEnum = RoleEnum;
  roleName: RoleEnum;

  @Output() locations = new EventEmitter<string[]>();

  loadingRetentionRate$: Observable<boolean>;

  features: BatchToggleFeatureElementDto[] = [];
  features$: Observable<UserFeatureDto[] | null>;
  premiumEndDate: Date | null = null;
  email: string;

  today = new Date();
  startDate = Constants.twoMonthsAgo;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private store: Store<AppStateInterface>,
    private translate: TranslateService,
    private toastr: ToastrService,
    private saveHistory: SaveUserHistoryService,
    private jwtService: JwtService,
    private sanitizer: DomSanitizer
  ) {
    super(store);
    this.companyName = this.route.snapshot.queryParamMap.get("companyName");
    if (this.companyName == null) this.router.navigate(["/dashboard"]);

    this.resultGlobalStatistics$ = this.store
      .pipe(select(resultGlobalStatisticsSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.loading$ = this.store
      .pipe(select(loadingSummaryStatisticsSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.resultRetentionRate$ = this.store
      .pipe(select(resultRetentionRateByWeeksSelectors))
      .pipe(takeUntil(this.ngDestroyed$));
    this.loadingGlobalStatics$ = this.store
      .pipe(select(loadingGlobalStaticsSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.lpProfile$ = this.store
      .pipe(select(resultLPInfoSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.resultSendStatics$ = this.store
      .pipe(select(sendMailSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.loadingRetentionRate$ = this.store
      .pipe(select(loadingRetentionRateSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.features$ = this.store
      .pipe(select(allFeaturesSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.form = this.fb.group({
      date: [[this.startDate, this.today]],
    });
    this.loadingAllCardStats$ = combineLatest([
      this.loadingGlobalStatics$
    ])
      .pipe(map((flags) => flags.some((f) => f)))
      .pipe(takeUntil(this.ngDestroyed$));

    const authorities = this.jwtService.getPermissions(jwtService.getToken());
    if (authorities) {
      this.forbidden = !authorities.includes(
        Authorities.LP_GETPROFILE.toString()
      );
    }

    this.roleName = jwtService.getRoleName(jwtService.getToken());
  }

  ngOnInit() {
    this.features$.subscribe((result) => {
      if (result) {
        const premiumFeature = result.filter(
          (feature) => feature.name === FeatureName.PREMIUM
        )?.[0];
        if (premiumFeature?.endDate) {
          this.premiumEndDate = premiumFeature.endDate;
        } else {
          this.premiumEndDate = null;
        }
      }
    });

    this.resultSendStatics$.subscribe((result: SuccessDto | null) => {
      if (result != null) {
        this.store.dispatch(resultSindingMail({ successMessage: null }));
        this.toastr.success(
          this.translate.instant("response.success." + result.message),
          this.translate.instant("response.successTitle")
        );
      }
    });
    if (this.translate.currentLang == "fr")
      this.companyActivities = Constants.IBERIS_ACTIVITIES_FR;
    else this.companyActivities = Constants.IBERIS_ACTIVITIES_EN;

    this.translate.onLangChange.subscribe(
      (langChangeEvent: LangChangeEvent) => {
        if (langChangeEvent.lang == "fr")
          this.companyActivities = Constants.IBERIS_ACTIVITIES_FR;
        else this.companyActivities = Constants.IBERIS_ACTIVITIES_EN;
      }
    );
    this.store.dispatch(
      resultGlobalStatisticsForLP({ resultGlobalStatisticsForLP: null })
    );
    this.store.dispatch(resultRetentionRateByWeeks({ retentionRate: null }));
    this.store.dispatch(resultLPInfo({ lpInfo: null }));
    this.store.dispatch(
      invokeGetLocationPartnerByCompanyName({
        companyName: this.companyName ?? "",
      })
    );

    this.lpProfile$.subscribe((location: LPInfo | null) => {
      if (location) {
        this.loadingProfile = false;
        this.lpProfile = {
          ...location,
          companyActivity:
            this.companyActivities.find(
              (value) => location.companyActivity == value.hashed_id
            )?.title ?? "",
        };
        this.fetchCardsStatistics(false);
        this.premiumEndDate = null;
        this.store.dispatch(invokeAllFeatures({ email: this.lpProfile.email }));
        this.email = this.lpProfile.email;
        this.inputAudience.emit({
          name: this.companyName,
          type: AudienceType.LOCATION,
          title: "Audiences",
          titleSpline: "invoices.summary",
          titleColumnChart: "invoices.distributionAgeGender",
          email: this.email,
        });
        this.search.emit({
          technicalCard: TechnicalCardSearchMethodEnum.LOCATION,
          value: this.companyName ?? "",
          locationPartner: null,
          finished: false,
        });
        this.fetchRetentionRate(false);
      }
    });

    this.resultGlobalStatistics$.subscribe((result) => {
      if (result) {
        this.resultGlobalStatistics = result;
        this.impression.value = result.impressions;
        this.cachedAt = result.cachedAt;
        this.reach.value = result.reach
      }
    });

    this.resultRetentionRate$.subscribe((result) => {
      if (result != null) {
        this.retentionRateCachedAt = result.cachedAt;
        this.retentionRate.emit(result);
        this.retentionChart = UtilFunctions.exportRetentionRate(
          result,
          this.translate
        );
      }
    });

    this.saveHistory.saveUserHistory(
      UserHistoryActionsEnum.GET,
      UserHistoryServicesEnum.LOCATION_PARTNER_STATISTICS
    );

    this.form.valueChanges.subscribe((value) => {
      this.getChartInDate(value.date);
    });
  }

  getChartInDate(date: Date[]) {
    this.dateFilter = {
      startDate: date[0],
      endDate: date[1],
    };
    this.fetchRetentionRate(false);
  }

  fetchRetentionRate(refreshCache: boolean) {
    this.fetchingRetentionRateState = RefreshButtonState.LOADING;
    this.store.dispatch(
      invokeRetentionRateByWeeks({
        email: this.email,
        filter: this.dateFilter,
        refreshCache,
      })
    );
    this.resultRetentionRate$
      .pipe(
        finalize(
          () => (this.fetchingRetentionRateState = RefreshButtonState.IDLE)
        )
      )
      .subscribe((result) => {
        if (result) {
          this.fetchingRetentionRateState = RefreshButtonState.SUCCESS;
          setTimeout(
            () => (this.fetchingRetentionRateState = RefreshButtonState.IDLE)
          );
        }
      });
  }

  fetchCardsStatistics(refreshCache: boolean) {
    if (refreshCache) {
      this.fetchingCardState = RefreshButtonState.LOADING;
    }
    this.store.dispatch(
      invokeLocationPartnerGlobalStatistics({
        email: this.lpProfile.email,
        refreshCache: refreshCache,
      })
    );
    if (refreshCache) {
      this.loadingAllCardStats$.subscribe((value) => {
        if (!value) {
          this.fetchingCardState = RefreshButtonState.SUCCESS;
          setTimeout(() => {
            this.fetchingCardState = RefreshButtonState.IDLE;
          }, 2500);
        } else {
          this.fetchingCardState = RefreshButtonState.LOADING;
        }
      });
    }
  }
  handleRetentionLoadingStateChange(loading: boolean) {
    this.isLoading = loading;
  }

  showlogo() {
    if (this.lpProfile?.logo) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(
        environment.CDN_RELEAD + this.lpProfile.logo
      );
    } else return null;
  }
}
